import { gql } from '@apollo/client'
import type { SecretFieldStatus, SecretKindEnum, SecretStatus, SetSecretArgs } from '@ztechapps/common.contract'
import { useList } from '../useList'
import { useActionMutation } from '../useMutation'
import { useError } from '../useError'

const SECRET_STATUS_QUERY = gql`
  query getSecretStatus($filter: JSON) {
    getSecretStatus(filter: $filter) {
      kind
      fields {
        name
        isSet
        tail
      }
    }
  }
`

const SET_SECRET_MUTATION = gql`
  mutation setSecret($input: SetSecretArgs!) {
    setSecret(input: $input) {
      kind
      fields {
        name
        isSet
        tail
      }
    }
  }
`

export type UseSecretProps = {
  kind: SecretKindEnum
  /** Does not load the status, for a caller the api would refuse (no `setting.canView`); `loading` and `error` then stay empty. */
  skip?: boolean
}

export type UseSecretResult = {
  /** Which fields of the kind are set, with the masked tail; never a value. */
  fields: SecretFieldStatus[]
  loading: boolean
  /** The error of loading the status, which the caller shows inline. */
  error: unknown
  saving: boolean
  /** Replaces the given fields (field name to the typed value); resolves false when the api refused it. */
  save: (secrets: Record<string, string>) => Promise<boolean>
}

/** The masked status of the tenant's secrets of one kind, and the write-only replace of their fields. */
export const useSecret = ({ kind, skip }: UseSecretProps): UseSecretResult => {
  const {
    list,
    loading,
    error: loadError,
    refetch,
  } = useList<SecretStatus>({
    query: SECRET_STATUS_QUERY,
    entity: 'getSecretStatus',
    filter: { kind },
    options: { queryOptions: { skip } },
  })
  const [execute, { error, loading: saving }] = useActionMutation<SecretStatus, SetSecretArgs>({
    query: SET_SECRET_MUTATION,
  })
  useError(error)

  const save = async (secrets: Record<string, string>): Promise<boolean> => {
    // The api rejects a failed request, which `useError` has already shown.
    const status = await execute({ kind, secrets }).catch(() => undefined)
    if (!status) return false

    await refetch()
    return true
  }

  return {
    fields: list?.find((status) => status.kind === kind)?.fields ?? [],
    loading,
    error: loadError,
    saving,
    save,
  }
}
